import React, { useEffect, useState } from 'react';
import { useSEO } from '../../hooks/useSEO';
import type { AboutContent, Certificate, Settings } from '../../types';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import OptimizedImage from '../../components/OptimizedImage';
import { useCollection } from '../../hooks/useCollection';

const XIcon: React.FC<{className?: string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;

const CertificateModal: React.FC<{ imageUrl: string, title: string, onClose: () => void }> = ({ imageUrl, title, onClose }) => {
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4 animate-fade-in" 
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label={`Certificate: ${title}`}
        >
            <div className="relative" onClick={e => e.stopPropagation()}>
                <OptimizedImage 
                    src={imageUrl} 
                    alt={title}
                    size={1200}
                    className="max-h-[90vh] max-w-full object-contain"
                />
                <button 
                    onClick={onClose}
                    className="absolute -top-12 right-0 text-white hover:text-gray-300 focus:outline-none"
                    aria-label="Close modal"
                >
                    <XIcon className="w-8 h-8"/>
                </button>
            </div>
             <style>{`
                @keyframes fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
            `}</style>
        </div>
    );
};


const AboutUsPage: React.FC<{ settings: Settings }> = ({ settings }) => {
    const [content, setContent] = useState<AboutContent | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const { data: certificates, isLoading: isLoadingCerts } = useCollection<Certificate>('certificates', { field: 'order', direction: 'asc' });
    const [viewingCert, setViewingCert] = useState<Certificate | null>(null);

    useSEO({
        title: `About Us | ${settings?.companyName || 'ARCH.'}`,
        description: content?.title || `Learn more about ${settings?.companyName || 'ARCH.'}, our design philosophy, history, and achievements.`,
        ogImage: content?.imageUrl || settings?.openGraph?.imageUrl
    }, [settings, content]);

    useEffect(() => {
        const fetchContent = async () => {
            const docRef = doc(db, 'aboutPage', 'main');
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setContent(docSnap.data() as AboutContent);
            }
            setIsLoading(false);
        };
        fetchContent();
        window.scrollTo(0, 0);
    }, []);

    if (isLoading || isLoadingCerts) {
        return <div className="h-screen flex items-center justify-center"><p>Loading...</p></div>;
    }

    if (!content) {
        return (
            <div className="pt-32 pb-16 text-center">
                <h1 className="text-5xl font-bold">About Us</h1>
                <p className="mt-4 text-gray-600">Content for this page has not been set up yet.</p>
            </div>
        );
    }

    return (
        <>
            {viewingCert && <CertificateModal imageUrl={viewingCert.imageUrl} title={viewingCert.title} onClose={() => setViewingCert(null)} />}
            <div className="bg-brand-light text-brand-dark">
                <div className="relative h-[70vh] w-full flex items-center justify-center text-white bg-brand-dark">
                    {content.imageUrl && (
                        <>
                            <OptimizedImage 
                                src={content.imageUrl}
                                size={1920}
                                alt={content.title}
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-50"></div>
                        </>
                    )}
                    <div className="relative z-10 text-center p-4">
                        <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight font-heading">{content.title}</h1>
                    </div>
                </div>
                
                <div className="container mx-auto px-6 max-w-4xl py-16">
                    {content.sections.map((section, index) => (
                        <div key={index} className="mb-12">
                            <h2 className="text-3xl font-bold font-heading mb-4 border-b-2 border-brand-dark pb-2">{section.title}</h2>
                            <div 
                                className="prose prose-lg max-w-none text-gray-800 leading-relaxed font-body"
                                dangerouslySetInnerHTML={{ __html: section.content }}
                            ></div>
                        </div>
                    ))}
                </div>
                
                {certificates && certificates.length > 0 && (
                    <div className="bg-gray-100 py-16">
                        <div className="container mx-auto px-6 max-w-6xl">
                            <h2 className="text-3xl font-bold font-heading text-center mb-12">Our Certifications & Accreditations</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                                {certificates.map(cert => (
                                    <div key={cert.id} className="group text-center">
                                        <button 
                                            onClick={() => setViewingCert(cert)}
                                            className="w-full bg-white p-4 shadow-md group-hover:shadow-xl transition-shadow duration-300"
                                            aria-label={`View certificate: ${cert.title}`}
                                        >
                                            <OptimizedImage 
                                                src={cert.imageUrl}
                                                size={400}
                                                alt={cert.title}
                                                className="w-full h-40 object-contain"
                                            />
                                        </button>
                                        <h3 className="text-md font-semibold mt-4">{cert.title}</h3>
                                        <p className="text-sm text-gray-500">{cert.issuingBody} - {cert.year}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                 <style>{`
                    .prose p { margin-bottom: 1.25em; }
                    .prose h1, .prose h2, .prose h3 { font-family: var(--font-family-heading); font-weight: 700; margin-top: 1.5em; margin-bottom: 1em; }
                `}</style>
            </div>
        </>
    );
};

export default AboutUsPage;
